import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEye, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import TablePagination from './TablePagination';
import { ApiLoadState, ApiLoadTableRow } from './ApiLoadState';
import { useDeleteMyToolMutation, useGetMyToolsQuery } from '../../redux/myToolsApi';
import { DEFAULT_PAGE_SIZE, getServerPagination } from '../../utils/pagination';
import {
  toolTypeLabel,
  formatToolCreatedAt,
  formatToolDate,
  listMyToolsFromResponse,
  mapMyToolRow,
  myToolProductCount,
} from '../../utils/billToFlowTools';

const LIST_COLUMNS = [
  'Sr. No.',
  'Tool Name',
  'Tool Code',
  'Tool Type',
  'Location',
  'Date',
  'SKUs',
  'Created On',
  'Created By',
  'Action',
];

function AdminMyTools() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = DEFAULT_PAGE_SIZE;
  const listQuery = useMemo(() => {
    const params = { page, per_page: pageSize };
    const q = search.trim();
    if (q) params.search = q;
    return params;
  }, [page, pageSize, search]);
  const { data: toolsRes, isLoading, isFetching, error, refetch } =
    useGetMyToolsQuery(listQuery);
  const [deleteMyTool, { isLoading: isDeleting }] = useDeleteMyToolMutation();
  const [toolToDelete, setToolToDelete] = useState(null);

  const tools = useMemo(
    () => listMyToolsFromResponse(toolsRes).map(mapMyToolRow).filter(Boolean),
    [toolsRes]
  );

  const server = getServerPagination(toolsRes, { page, pageSize });
  const currentPage = server.currentPage;
  const totalCount = server.totalCount || tools.length;
  const effectivePageSize = server.pageSize || pageSize;

  const handleConfirmDelete = async () => {
    const id = toolToDelete?.id;
    if (id == null || String(id).trim() === '') return;
    try {
      await deleteMyTool(id).unwrap();
      toast.success('Tool deleted successfully.');
      setToolToDelete(null);
    } catch (err) {
      toast.error(err?.data?.message || err?.data?.error || 'Failed to delete tool.');
    }
  };

  return (
    <DashboardLayout title="My Tools">
      <div className="space-y-4 py-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full min-w-0 md:max-w-xs">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search My Tools"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
            <Link
              to="/dashboard/admin/build-to-flow/create"
              className="inline-flex shrink-0 items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm"
            >
              Create Projected Tool
            </Link>
          </div>

          <ApiLoadState
            isLoading={isLoading}
            isFetching={isFetching}
            hasData={tools.length > 0}
            error={error}
            refetch={refetch}
            message="Loading my tools…"
            className="py-8"
          />

          {!error && !isLoading && !isFetching && tools.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              {search.trim()
                ? 'No tools match your search.'
                : 'No tools created yet. Use Create Projected Tool to add one.'}
            </p>
          ) : null}

          {!error && tools.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr>
                    {LIST_COLUMNS.map((label) => (
                      <th
                        key={label}
                        className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <ApiLoadTableRow
                    colSpan={LIST_COLUMNS.length}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    hasData={tools.length > 0}
                    error={error}
                    refetch={refetch}
                    message="Loading my tools..."
                  />
                  {tools.map((tool, index) => (
                    <tr key={tool.id || index} className="hover:bg-amber-50/40">
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {(currentPage - 1) * effectivePageSize + index + 1}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 font-medium text-gray-900">
                        {tool.toolName || '—'}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {tool.toolCode || '—'}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {toolTypeLabel(tool)}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {tool.locationName || '—'}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {formatToolDate(tool.toolDate)}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700 tabular-nums">
                        {myToolProductCount(tool) || '—'}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {formatToolCreatedAt(tool.createdAt)}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {tool.createdBy || '—'}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5">
                        <div className="flex items-center gap-1">
                          <Link
                            to={`/dashboard/admin/build-to-flow/my-tools/${tool.id}`}
                            state={{ tool }}
                            className="inline-flex rounded-lg p-1.5 text-red-700 hover:bg-amber-50/60"
                            aria-label="View tool"
                            title="View"
                          >
                            <FaEye className="h-5 w-5" />
                          </Link>
                          <button
                            type="button"
                            onClick={() => setToolToDelete(tool)}
                            disabled={isDeleting}
                            className="inline-flex rounded-lg p-1.5 text-red-700 hover:bg-amber-50/60 disabled:opacity-50"
                            aria-label="Delete tool"
                            title="Delete"
                          >
                            <FaTrash className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <TablePagination
            totalCount={totalCount}
            currentPage={currentPage}
            onPageChange={setPage}
            pageSize={effectivePageSize}
          />
        </div>
      </div>
      {toolToDelete ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Delete tool confirmation"
          onClick={isDeleting ? undefined : () => setToolToDelete(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-900">Delete Tool</h2>
            <p className="mt-2 text-sm text-gray-600">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-gray-900">
                {toolToDelete.toolName || toolToDelete.toolCode || 'this tool'}
              </span>
              ?
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setToolToDelete(null)}
                disabled={isDeleting}
                className="rounded-lg border border-[#e8e4df] bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-[#faf8f5] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </DashboardLayout>
  );
}

export default AdminMyTools;
